/**
 * Safely extract a user-facing error message from API/axios errors.
 *
 * The backend's HttpExceptionFilter wraps the original Nest payload, so
 * `response.data.message` is frequently an OBJECT
 * `{ message, error, statusCode }` rather than a string. Rendering that
 * object into JSX throws React error #31 ("Objects are not valid as a React
 * child"), which crashes the whole admin app while trying to display an
 * error. Everything user-facing MUST go through this helper.
 */
interface AxiosLikeError {
  response?: { data?: unknown };
  message?: unknown;
}

function firstString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value : null;
  }
  if (Array.isArray(value)) {
    // NestJS class-validator returns message as string[]
    const parts = value
      .map((v) => (typeof v === 'string' ? v : firstString(v)))
      .filter((v): v is string => !!v && v.trim().length > 0);
    return parts.length > 0 ? parts.join('; ') : null;
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return firstString(obj.message) ?? firstString(obj.error);
  }
  return null;
}

export function toErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (typeof err === 'string') {
    return err.trim().length > 0 ? err : fallback;
  }
  if (!err || typeof err !== 'object') return fallback;

  const anyErr = err as AxiosLikeError;
  const data = anyErr.response?.data;

  if (data && typeof data === 'object') {
    const fromData = firstString(data);
    if (fromData) return fromData;
  } else if (typeof data === 'string') {
    // Non-JSON responses can be a whole HTML error page — never surface that.
    const trimmed = data.trim();
    if (trimmed.length > 0 && trimmed.length <= 200 && !trimmed.startsWith('<')) {
      return trimmed;
    }
  }

  const fromError = firstString(anyErr.message);
  if (fromError) return fromError;

  return fallback;
}

/** @deprecated use `toErrorMessage` — kept so existing call sites keep working. */
export function extractErrorMessage(err: unknown, fallback: string): string {
  return toErrorMessage(err, fallback);
}
